import {AlertIcon, ArrowLeftIcon, BookIcon, CodescanIcon, ShieldCheckIcon, ShieldLockIcon} from '@primer/octicons-react'
import {
  ThemeProvider,
  Hero,
  AnchorNav,
  SectionIntro,
  FAQ,
  FAQGroup,
  Bento,
  Box,
  Text,
  River,
  Heading,
  RiverBreakout,
  Stack,
  Card,
  OrderedList,
  UnorderedList,
  Button,
  Pillar,
  Image,
  Label,
  LogoSuite,
} from '@primer/react-brand'

export function SecurityIndex() {
  return (
    <ThemeProvider colorMode="light" className="lp-has-fontSmoothing lp-textWrap--pretty">
      <ThemeProvider colorMode="dark">
        <section id="hero" className="lp-Hero">
          <div className="lp-Container">
            {/* Make space for header + nav */}
            <div className="lp-Spacer" style={{height: '137px'}} aria-hidden />

            <Hero data-hpc>
              <Hero.Label size="large">GitHub Advanced Security</Hero.Label>
              <Hero.Heading size="1" weight="bold">
                Baked in. Not bolted on.
              </Hero.Heading>
              <Hero.Description>
                <Text size="400" weight="normal" variant="muted">
                  GitHub Advanced Security natively detects, prevents, and fixes vulnerabilities 7x faster than external
                  application security tools.
                </Text>
              </Hero.Description>
              <Hero.PrimaryAction href="#TODO">Contact sales</Hero.PrimaryAction>
              <Hero.SecondaryAction href="#TODO" hasArrow={false}>
                Request a demo
              </Hero.SecondaryAction>
            </Hero>

            <LogoSuite hasDivider={false}>
              <LogoSuite.Heading visuallyHidden>GitHub Security is used by</LogoSuite.Heading>
              <LogoSuite.Logobar marquee marqueeSpeed="slow">
                <Image
                  src="/images/modules/site/security/2024/logos/coca-cola.svg"
                  alt="Coca Cola's logo"
                  height={40}
                />
                <Image
                  src="/images/modules/site/security/2024/logos/coyote-logistics.svg"
                  alt="Coyote Logistics's logo"
                  height={40}
                />
                <Image src="/images/modules/site/security/2024/logos/duolingo.svg" alt="Duolingo's logo" height={40} />
                <Image src="/images/modules/site/security/2024/logos/stripe.svg" alt="Stripe's logo" height={44} />
                <Image src="/images/modules/site/security/2024/logos/shopify.svg" alt="Shopify's logo" height={40} />
                <Image
                  src="/images/modules/site/security/2024/logos/mercado-libre.svg"
                  alt="Mercado Libre's logo"
                  height={40}
                />
                <Image
                  src="/images/modules/site/security/2024/logos/mercedes.svg"
                  alt="Mercedes Benz's logo"
                  height={48}
                />
                <Image src="/images/modules/site/security/2024/logos/fidelity.svg" alt="Fidelity's logo" height={36} />
                <Image src="/images/modules/site/security/2024/logos/dell.svg" alt="Dell's logo" height={60} />
                <Image src="/images/modules/site/security/2024/logos/lemonade.svg" alt="Lemonade's logo" height={36} />
                <Image
                  src="/images/modules/site/security/2024/logos/sas.svg"
                  alt="Scandinavian Airlines's logo"
                  height={36}
                />
                <Image src="/images/modules/site/security/2024/logos/itau.svg" alt="Itau's logo" height={50} />
                <Image src="/images/modules/site/security/2024/logos/hover.svg" alt="Hover's logo" height={36} />
                <Image src="/images/modules/site/security/2024/logos/lyft.svg" alt="Lyft's logo" height={64} />
              </LogoSuite.Logobar>
            </LogoSuite>

            <Box className="lp-AnchorNav" style={{height: 0}}>
              <AnchorNav hideUntilSticky>
                <AnchorNav.Link href="#overview">Overview</AnchorNav.Link>
                <AnchorNav.Link href="#features">Features</AnchorNav.Link>
                <AnchorNav.Link href="#pricing">Pricing</AnchorNav.Link>
                <AnchorNav.Link href="#faq">FAQs</AnchorNav.Link>
                <AnchorNav.Action href="#TODO">Contact sales</AnchorNav.Action>
                <AnchorNav.SecondaryAction href="#TODO">Request a demo</AnchorNav.SecondaryAction>
              </AnchorNav>
            </Box>
          </div>
        </section>

        <section id="overview" className="lp-Section--overview">
          <div className="lp-Container">
            <div className="lp-Spacer lp-Spacer--72 lp-Spacer--128xl" aria-hidden />

            <SectionIntro align="center" fullWidth>
              <SectionIntro.Label size="large" className="lp-SectionIntroLabel--muted">
                Overview
              </SectionIntro.Label>
              <SectionIntro.Heading as="h2" size="2" weight="semibold">
                Fixes in minutes, not months.
              </SectionIntro.Heading>
            </SectionIntro>

            <div className="lp-Spacer lp-Spacer--56 lp-Spacer--88xl" aria-hidden />

            {/* Bento 1 */}
            <Bento>
              <Bento.Item columnSpan={{xsmall: 12, medium: 7}} rowSpan={{xsmall: 4, medium: 5}} visualAsBackground>
                <Bento.Content horizontalAlign="center" verticalAlign="center">
                  <Bento.Heading as="h3" size="4" weight="semibold">
                    Accelerate AppSec workflows.
                  </Bento.Heading>
                </Bento.Content>
                <Bento.Visual>
                  <Image
                    src="/images/modules/site/security/2024/overview-bento-1.jpg"
                    alt=""
                    width="724"
                    height="500"
                    loading="lazy"
                  />
                </Bento.Visual>
              </Bento.Item>

              {/* Bento 2 */}
              <Bento.Item
                columnSpan={{xsmall: 12, medium: 5}}
                rowSpan={{xsmall: 5, medium: 5}}
                className="lp-Overview-bentoBackgroundGradient"
                style={{gridTemplateRows: '1fr 1fr', rowGap: '48px'}}
              >
                <Bento.Visual fillMedia={false} horizontalAlign="center" verticalAlign="end">
                  <Image
                    src="/images/modules/site/security/2024/overview-bento-2.png"
                    alt=""
                    width="120"
                    height="120"
                    loading="lazy"
                  />
                </Bento.Visual>
                <Bento.Content horizontalAlign="center" verticalAlign="start">
                  <Bento.Heading
                    as="h3"
                    className="lp-is-sansSerifAlt"
                    style={{fontSize: '80px', marginBottom: '24px', lineHeight: '100%'}}
                  >
                    48%
                  </Bento.Heading>
                  <Text as="div" variant="muted">
                    Vulnerabilities fixed in real time, <br className="lp-breakWhenWide" /> more than 7x the industry
                    average.<sup>1</sup>
                  </Text>
                </Bento.Content>
              </Bento.Item>

              {/* Bento 3 */}
              <Bento.Item
                columnSpan={{xsmall: 12, medium: 5}}
                rowSpan={{xsmall: 5, medium: 5}}
                visualAsBackground
                className="lp-Overview-bentoBackgroundGradient"
              >
                <Bento.Content horizontalAlign="center" verticalAlign="center" leadingVisual={<CodescanIcon />}>
                  <Bento.Heading
                    as="h3"
                    className="lp-is-sansSerifAlt"
                    style={{fontSize: '56px', marginBottom: '24px', lineHeight: '100%'}}
                  >
                    2.4x <br /> more <br /> precise.
                  </Bento.Heading>
                  <Text as="div" variant="muted">
                    Finds leaked secrets with <br className="lp-breakWhenWide" /> fewest false positives.<sup>2</sup>
                  </Text>
                </Bento.Content>
              </Bento.Item>

              {/* Bento 4 */}
              <Bento.Item
                columnSpan={{xsmall: 12, medium: 7}}
                rowSpan={{xsmall: 4, medium: 5}}
                style={{
                  backgroundSize: 'cover',
                  backgroundImage: 'url("/images/modules/site/security/2024/overview-bento-4.jpg")',
                  gridTemplateRows: '1fr 1fr',
                  rowGap: '24px',
                }}
              >
                <Bento.Content horizontalAlign="center" verticalAlign="end" leadingVisual={<ShieldLockIcon />}>
                  <Bento.Heading as="h3" size="4" weight="semibold">
                    Secrets, kept.
                  </Bento.Heading>
                </Bento.Content>
                <Bento.Visual fillMedia={false} padding="condensed" horizontalAlign="center" verticalAlign="start">
                  <Image
                    src="/images/modules/site/security/2024/overview-bento-4-visual.webp"
                    alt=""
                    width="461"
                    height="118"
                    loading="lazy"
                  />
                </Bento.Visual>
              </Bento.Item>

              {/* Bento 5 */}
              <Bento.Item
                columnSpan={{xsmall: 12, medium: 7}}
                rowSpan={{xsmall: 4, medium: 5}}
                style={{
                  backgroundSize: 'cover',
                  backgroundImage: 'url("/images/modules/site/security/2024/overview-bento-5.jpg")',
                  gridTemplateRows: '1fr 1fr',
                  rowGap: '24px',
                }}
              >
                <Bento.Content horizontalAlign="center" verticalAlign="end">
                  <Bento.Heading as="h3" size="4" weight="semibold" style={{maxWidth: '20ch'}}>
                    AI-powered code suggestions automate secure coding practices.
                  </Bento.Heading>
                </Bento.Content>
                <Bento.Visual fillMedia={false} padding="condensed" horizontalAlign="center" verticalAlign="start">
                  <Image
                    src="/images/modules/site/security/2024/overview-bento-5-visual.webp"
                    alt=""
                    width="622"
                    height="180"
                    loading="lazy"
                  />
                </Bento.Visual>
              </Bento.Item>

              {/* Bento 6 */}
              <Bento.Item
                columnSpan={{xsmall: 12, medium: 5}}
                rowSpan={{xsmall: 4, medium: 5}}
                visualAsBackground
                className="lp-Overview-bentoBackgroundGradient"
              >
                <Bento.Content horizontalAlign="center" verticalAlign="center" leadingVisual={<ShieldCheckIcon />}>
                  <Bento.Heading as="h3" size="4" weight="semibold">
                    Keep new vulnerabilities out of your code.
                  </Bento.Heading>
                </Bento.Content>
              </Bento.Item>
            </Bento>

            <div className="lp-Spacer lp-Spacer--96 lp-Spacer--152xl" aria-hidden />
          </div>
        </section>
      </ThemeProvider>

      <section id="features">
        <div className="lp-Container">
          <div className="lp-Spacer lp-Spacer--72 lp-Spacer--128xl" aria-hidden />

          <SectionIntro align="center">
            <SectionIntro.Label size="large" className="lp-SectionIntroLabel--muted">
              Features
            </SectionIntro.Label>
            <SectionIntro.Heading as="h2" size="1" weight="bold" className="lp-textWrap--balance">
              For developers who love to code.
            </SectionIntro.Heading>
            <SectionIntro.Description className="lp-Features-sectionIntroDescription">
              <strong>Detect, prevent, and fix vulnerabilities without leaving your flow.</strong>
              <br className="lp-break-whenWide" /> Add GitHub Advanced Security to your GitHub Enterprise plan for
              AI-powered code scanning, secret scanning, and supply chain security
            </SectionIntro.Description>
          </SectionIntro>

          <div className="lp-Spacer lp-Spacer--40 lp-Spacer--72xl" aria-hidden />

          <Stack direction={{narrow: 'vertical', regular: 'horizontal'}} gap="normal" padding="none">
            <Pillar className="lp-Features-pillarCard">
              <Pillar.Icon icon={<AlertIcon />} color="blue" />
              <Pillar.Heading>Detect</Pillar.Heading>
              <Pillar.Description>
                All kinds of automated scan run at every level to catch anything before it gets to production. Our ever
                expanding libraries of actions are available to help you stay secure.
              </Pillar.Description>
            </Pillar>
            <Pillar className="lp-Features-pillarCard">
              <Pillar.Icon icon={<CodescanIcon />} color="blue" />
              <Pillar.Heading>Prevent</Pillar.Heading>
              <Pillar.Description>
                Erasmus said “Better prevent than cure” - we agree, that’s why believe in community and automation to
                actively stay on top of threats and vulnerabilities.
              </Pillar.Description>
            </Pillar>
            <Pillar className="lp-Features-pillarCard">
              <Pillar.Icon icon={<ShieldLockIcon />} color="blue" />
              <Pillar.Heading>Remediation</Pillar.Heading>
              <Pillar.Description>
                Bots, standardized processes, and AI suggestion will provide everything you need to fix things whenever
                and wherever you need, whatever the codebase.
              </Pillar.Description>
            </Pillar>
          </Stack>

          <div className="lp-Spacer lp-Spacer--64 lp-Spacer--96xl" aria-hidden />

          {/* Detect features */}

          {/* TODO: Is this the correct design? */}
          <Pillar align="center" style={{maxWidth: 'none'}}>
            <Pillar.Icon icon={<AlertIcon />} color="blue" />
            <Pillar.Heading style={{marginBottom: '0'}}>Detect features</Pillar.Heading>
          </Pillar>

          <div className="lp-Spacer lp-Spacer--64 lp-Spacer--96xl" aria-hidden />

          <RiverBreakout style={{paddingTop: '0'}}>
            <RiverBreakout.A11yHeading>TODO</RiverBreakout.A11yHeading>
            <RiverBreakout.Visual aria-hidden="true">
              <Image
                src="/images/modules/site/security/2024/features-river-1.webp"
                alt="Screenshot of a code injection warning in a software development environment, showing a piece of JavaScript code with a security vulnerability highlighted."
                width="1248"
                height="647"
                style={{display: 'block'}}
                loading="lazy"
              />
            </RiverBreakout.Visual>
            <RiverBreakout.Content
              trailingComponent={() => (
                <Text>
                  <strong>On schedule, on demand.</strong> Code scanning helps you find, triage, and prioritize existing
                  security vulnerabilities across up to 1,000 repositories at once, and flag new vulnerabilities in
                  every push.
                </Text>
              )}
            >
              <Text>Get clean, stay clean.</Text>
            </RiverBreakout.Content>
          </RiverBreakout>

          <River imageTextRatio="60:40">
            <River.Content>
              <Heading as="h3" size="5">
                <Label className="lp-Label--inRiver">Available in public beta, March, 2024</Label>
                AI-powered security fixes.
              </Heading>
              <Text as="p" className="lp-River-text">
                Resolve vulnerabilities in real time with AI-powered code suggestions in the pull request. With GitHub
                Advanced Security, you don’t need to be a security expert to write secure code.
              </Text>
            </River.Content>
            <River.Visual>
              <Image
                src="/images/modules/site/security/2024/features-river-2.webp"
                alt="Notification from a GitHub bot suggesting a security fix for a code vulnerability related to file path construction with user input."
                width="708"
                height="472"
                style={{display: 'block'}}
                loading="lazy"
              />
            </River.Visual>
          </River>

          <div className="lp-Spacer lp-Spacer--48 lp-Spacer--104xl" aria-hidden />

          {/* Prevent features */}

          <Pillar align="center" style={{maxWidth: 'none'}}>
            <Pillar.Icon icon={<CodescanIcon />} color="blue" />
            <Pillar.Heading style={{marginBottom: '0'}}>Prevent features</Pillar.Heading>
          </Pillar>

          <div className="lp-Spacer lp-Spacer--48" aria-hidden />

          <River imageTextRatio="60:40">
            <River.Content>
              <Heading as="h3" size="5">
                Secure your secrets, protect your business.
              </Heading>
              <Text as="p" className="lp-River-text">
                Secret scanning with push protection guards over 200 token types and patterns from more than 180 service
                providers, including secrets unique to your organization.
                <div className="lp-Features-riverStat lp-is-sansSerifAlt">36,000</div>
                <div>Secret leaks fixed in 2023</div>
              </Text>
            </River.Content>
            <River.Visual>
              <Image
                src="/images/modules/site/security/2024/features-river-3.webp"
                alt="Terminal output displaying a GitHub error message 'GH009: Secrets detected! This push failed.' with a request to resolve secrets before pushing again."
                width="708"
                height="472"
                style={{display: 'block'}}
                loading="lazy"
              />
            </River.Visual>
          </River>

          <div className="lp-Spacer lp-Spacer--16" aria-hidden />

          <RiverBreakout>
            <RiverBreakout.A11yHeading>TODO</RiverBreakout.A11yHeading>
            <RiverBreakout.Visual aria-hidden="true">
              <Image
                src="/images/modules/site/security/2024/features-river-4.webp"
                alt="Colorful line graph showing the number of software vulnerabilities over time, categorized by critical, high, moderate, and low severities."
                width="1248"
                height="647"
                style={{display: 'block'}}
                loading="lazy"
              />
            </RiverBreakout.Visual>
            <RiverBreakout.Content>
              <Text>
                <em>Security status at a glance.</em> Verify progress and track trends to prioritize remediation tasks
                across multiple repositories.
              </Text>
            </RiverBreakout.Content>
          </RiverBreakout>

          <River imageTextRatio="60:40">
            <River.Content>
              <Heading as="h3" size="5">
                Trusted expertise.
              </Heading>
              <Text as="p" className="lp-River-text">
                Understand, prioritize, and remediate contextual vulnerability intelligence with confidence.
              </Text>
            </River.Content>
            <River.Visual>
              <Image
                src="/images/modules/site/security/2024/features-river-5.webp"
                alt="List of security advisories with critical, moderate, and low severity ratings, including CVE references and the time since publication."
                width="708"
                height="472"
                style={{display: 'block'}}
                loading="lazy"
              />
            </River.Visual>
          </River>

          <div className="lp-Spacer lp-Spacer--48 lp-Spacer--104xl" aria-hidden />

          {/* Fix features */}

          <Pillar align="center" style={{maxWidth: 'none'}}>
            <Pillar.Icon icon={<ShieldLockIcon />} color="blue" />
            <Pillar.Heading style={{marginBottom: '0'}}>Fix features</Pillar.Heading>
          </Pillar>

          <div className="lp-Spacer lp-Spacer--48" aria-hidden />

          <River imageTextRatio="60:40">
            <River.Content>
              <Heading as="h3" size="5">
                Instant gratification.
              </Heading>
              <Text as="p" className="lp-River-text">
                Push-button enablement, automatic configuration, and integrations with ticketing tools speed deployment
                across your development and quality teams.
              </Text>
            </River.Content>
            <River.Visual>
              <Image
                src="/images/modules/site/security/2024/features-river-6.webp"
                alt="Settings panel for Dependabot Alerts and security features with enable buttons for various automated security and version update checks."
                width="708"
                height="472"
                style={{display: 'block'}}
                loading="lazy"
              />
            </River.Visual>
          </River>

          <River imageTextRatio="60:40">
            <River.Content>
              <Heading as="h3" size="5">
                Dependencies you can depend on.
              </Heading>
              <Text as="p" className="lp-River-text">
                Secure, manage, and report on software supply chains with automated security and version updates and
                one-click software bills of material (SBOMs).
                <div className="lp-Features-riverStat lp-is-sansSerifAlt">60M</div>
                <div>Outdated and vulnerable dependencies resolved each year with Dependabot.</div>
              </Text>
            </River.Content>
            <River.Visual>
              <Image
                src="/images/modules/site/security/2024/features-river-7.webp"
                alt="Open security alert for 'Improper Input Validation in PyYAML' on GitHub, suggesting an update to the package version to fix vulnerabilities."
                width="708"
                height="472"
                style={{display: 'block'}}
                loading="lazy"
              />
            </River.Visual>
          </River>

          <div className="lp-Spacer lp-Spacer--48 lp-Spacer--120xl" aria-hidden />
        </div>
      </section>

      <section id="pricing" className="lp-Section--pricing">
        <div className="lp-Container">
          <div className="lp-Spacer lp-Spacer--48 lp-Spacer--104xl" aria-hidden />

          <SectionIntro align="center" fullWidth>
            <SectionIntro.Label size="large" className="lp-SectionIntroLabel--muted">
              Pricing
            </SectionIntro.Label>
            <SectionIntro.Heading size="3">
              Native security <br className="lp-break-whenWide" /> for every repository.
            </SectionIntro.Heading>
            <SectionIntro.Description>
              Eliminate toolchain cost and complexity with native security tools for GitHub Enterprise and Azure DevOps.
            </SectionIntro.Description>
          </SectionIntro>

          <div className="lp-Spacer lp-Spacer--40" aria-hidden />

          <Stack
            className="lp-Pricing-card"
            direction={{narrow: 'vertical', regular: 'horizontal'}}
            gap="none"
            padding="none"
          >
            {/* Standard */}

            <Stack padding="none" gap={24} style={{flex: '1'}}>
              <Box>
                <Text size="100" weight="semibold" className="lp-Pricing-label">
                  Included
                </Text>
                <Box marginBlockStart={8} marginBlockEnd={16}>
                  <Heading as="h3" size="5">
                    Standard Security
                  </Heading>
                </Box>
                <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description">
                  Manage and secure open source software and dependencies.
                </Text>
              </Box>

              <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                <Text weight="normal" className="lp-Pricing-word">
                  Included
                </Text>
                <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                  For all users <br /> and plans
                </Text>
              </Stack>

              <hr className="lp-Pricing-divider" />

              <UnorderedList variant="checked" className="lp-Pricing-list">
                <UnorderedList.Item>Dependabot alerts</UnorderedList.Item>
                <UnorderedList.Item>Dependabot version and security updates</UnorderedList.Item>
                <UnorderedList.Item>Security overview</UnorderedList.Item>
                <UnorderedList.Item>Advisory database</UnorderedList.Item>
                <UnorderedList.Item>Vulnerability reporting</UnorderedList.Item>
                <UnorderedList.Item>Leaked secret partner notifications (public repositories)</UnorderedList.Item>
              </UnorderedList>

              <Button
                as="a"
                href="https://docs.github.com/code-security/getting-started/github-security-features"
                block
              >
                Learn more
              </Button>
            </Stack>

            <hr className="lp-Pricing-divider lp-Pricing-divider--vertical" />

            {/* Advanced */}

            <Stack padding="none" gap={24} style={{flex: '1'}}>
              <Box>
                <Text size="100" weight="semibold" className="lp-Pricing-label">
                  Add-on for GitHub Enterprise
                </Text>
                <Box marginBlockStart={8} marginBlockEnd={16}>
                  <Heading as="h3" size="5">
                    GitHub Advanced Security
                  </Heading>
                </Box>
                <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description">
                  Detect, prevent, and remediate vulnerabilities in all of your repositories (requires GitHub Enterprise
                  or Azure DevOps).
                </Text>
              </Box>

              <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                <Text size="500" weight="normal" className="lp-is-sansSerifAlt" style={{lineHeight: 1.4}}>
                  $
                </Text>
                <Text weight="normal" className="lp-Pricing-nr lp-is-sansSerifAlt">
                  49
                </Text>
                <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                  per month / <br /> per active committer
                </Text>
              </Stack>

              <hr className="lp-Pricing-divider" />

              <Box style={{flex: '1'}}>
                <Box className="lp-Pricing-plus">
                  <ArrowLeftIcon size={16} />
                  <Text size="200" weight="normal" className="lp-Pricing-plusText">
                    All standard security features, plus:
                  </Text>
                </Box>

                <UnorderedList variant="checked" className="lp-Pricing-list">
                  <UnorderedList.Item>Code scanning with CodeQL</UnorderedList.Item>
                  <UnorderedList.Item>AI-powered autofix*</UnorderedList.Item>
                  <UnorderedList.Item>Secret scanning alerts for all repositories</UnorderedList.Item>
                  <UnorderedList.Item>Secret scanning push protection</UnorderedList.Item>
                  <UnorderedList.Item>Dependency review</UnorderedList.Item>
                  <UnorderedList.Item>Auto-triage rules for Dependabot</UnorderedList.Item>
                  <UnorderedList.Item>Push-button enablement</UnorderedList.Item>
                  <UnorderedList.Item>Security overview dashboards</UnorderedList.Item>
                </UnorderedList>
              </Box>

              <Button as="a" href="https://github.com/enterprise/contact" variant="primary">
                Contact sales
              </Button>
            </Stack>
          </Stack>

          <div className="lp-Spacer lp-Spacer--72 lp-Spacer--128xl" aria-hidden />
        </div>
      </section>

      <section id="resources">
        <div className="lp-Container">
          <div className="lp-Spacer lp-Spacer--48 lp-Spacer--104xl" aria-hidden />

          <SectionIntro align="center" fullWidth>
            <SectionIntro.Heading size="3">
              Get the most out of <br className="lp-break-whenWide" /> GitHub Advanced Security.
            </SectionIntro.Heading>
          </SectionIntro>

          <div className="lp-Spacer lp-Spacer--40" aria-hidden />

          <Stack
            direction={{narrow: 'vertical', regular: 'horizontal', wide: 'horizontal'}}
            gap="normal"
            padding="none"
          >
            <div className="lp-Resources-card">
              <Card
                ctaText="Get started with GHAS"
                href="https://docs.github.com/code-security/getting-started/github-security-features"
              >
                <Card.Icon icon={<BookIcon />} color="blue" hasBackground />
                <Card.Heading>How to get started with GitHub Advanced Security (GHAS)</Card.Heading>
                <Card.Description>
                  Learn about GitHub Advanced Security and how it can benefit your organization.
                </Card.Description>
              </Card>
            </div>
            <div className="lp-Resources-card">
              <Card
                ctaText="Read the Forrester Report"
                href="https://resources.github.com/forrester-industry-spotlight-github-advanced-security/"
              >
                <Card.Icon icon={<BookIcon />} color="blue" hasBackground />
                <Card.Heading>GitHub TEI spotlight for GitHub Advanced Security</Card.Heading>
                <Card.Description>
                  Read about the benefits of improving software security standards in organizations
                </Card.Description>
              </Card>
            </div>
            <div className="lp-Resources-card">
              <Card
                ctaText="Watch the videos"
                href="https://resources.github.com/security/what-is-github-advanced-security/"
              >
                <Card.Icon icon={<BookIcon />} color="blue" hasBackground />
                <Card.Heading>Security playlist</Card.Heading>
                <Card.Description>
                  Learn how industry experts use GitHub Advanced Security to protect their code without sacrificing
                  developer productivity.
                </Card.Description>
              </Card>
            </div>
          </Stack>
        </div>
      </section>

      <section id="faq">
        <div className="lp-Container">
          <div className="lp-Spacer lp-Spacer--72 lp-Spacer--128xl" aria-hidden />

          <FAQGroup>
            <FAQGroup.Heading align="center">Frequently asked questions.</FAQGroup.Heading>

            <FAQ>
              <FAQ.Heading>Group 1</FAQ.Heading>
              <FAQ.Item>
                <FAQ.Question>Question 1</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 1</p>
                </FAQ.Answer>
              </FAQ.Item>
              <FAQ.Item>
                <FAQ.Question>Question 2</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 2</p>
                </FAQ.Answer>
              </FAQ.Item>
              <FAQ.Item>
                <FAQ.Question>Question 3</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 3</p>
                </FAQ.Answer>
              </FAQ.Item>
            </FAQ>

            <FAQ>
              <FAQ.Heading>Group 2</FAQ.Heading>
              <FAQ.Item>
                <FAQ.Question>Question 1</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 1</p>
                </FAQ.Answer>
              </FAQ.Item>
              <FAQ.Item>
                <FAQ.Question>Question 2</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 2</p>
                </FAQ.Answer>
              </FAQ.Item>
              <FAQ.Item>
                <FAQ.Question>Question 3</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 3</p>
                </FAQ.Answer>
              </FAQ.Item>
            </FAQ>
          </FAQGroup>

          <div className="lp-Spacer lp-Spacer--72 lp-Spacer--128xl" aria-hidden />
        </div>
      </section>

      <section id="footnotes">
        <div className="lp-Container">
          <div className="lp-Spacer lp-Spacer--40" aria-hidden />

          <OrderedList>
            <OrderedList.Item className="lp-Footnotes-item">
              Based on data from the industry’s longest running analysis of fix rates, Veracode State of Software
              Security 2023. Developers with GitHub Advanced Security fix 72% of vulnerabilities within 28 days, more
              than 7x faster than the industry average, where it takes 198 days to reach a 50% fix rate.
            </OrderedList.Item>
          </OrderedList>

          <div className="lp-Spacer lp-Spacer--64" aria-hidden />
        </div>
      </section>
    </ThemeProvider>
  )
}

try{ SecurityIndex.displayName ||= 'SecurityIndex' } catch {}